// theme.js
// ─────────────────────────────────────────────────────────────────────────────
// THE DESIGN SYSTEM
// One source of truth for colors, type, spacing, motion. Import from this file
// instead of hard-coding hex values inside components. If you find yourself
// reaching for a color that isn't here, the system needs to grow — don't shim.
// ─────────────────────────────────────────────────────────────────────────────

export const tokens = {
  // ─── COLOR ─────────────────────────────────────────────────────────────────
  color: {
    // Hero / brand — deep blueprint navy. Authority without being corporate-blue.
    primary:        "#1B3A57",
    primaryHover:   "#15304A",
    primarySoft:    "#E8EEF3",   // tint for backgrounds
    primaryAccent:  "#3B6FA0",   // mid-tone for emphasis on dark surfaces

    // Signature accent — safety orange. Use ONLY for primary actions,
    // critical highlights, the one thing on screen that demands attention.
    accent:         "#FF6B35",
    accentHover:    "#E55A28",
    accentSoft:     "#FFF1EA",

    // Status — domain-coded for construction
    success:        "#1F7A4D",   // forest green — approved, certified
    successSoft:    "#E3F1E9",
    successBorder:  "#A8D4BB",

    warning:        "#C77700",   // amber — pending, in review
    warningSoft:    "#FBF0DD",
    warningBorder:  "#E8C57A",

    danger:         "#C73B2A",   // brick red — rejected, overdue, negative
    dangerSoft:     "#F8E5E2",
    dangerBorder:   "#E0A199",

    info:           "#1B6EA8",   // info, links
    infoSoft:       "#E3EFF7",
    infoBorder:     "#9CC4DF",

    neutral:        "#5D6470",   // draft, neutral states
    neutralSoft:    "#EFEEEA",
    neutralBorder:  "#C8C5BC",

    // Surfaces — warm off-white instead of cold blue-grey
    canvas:         "#FAFAF7",   // page background (architectural paper)
    surface:        "#FFFFFF",   // cards, panels
    surfaceAlt:     "#F4F2EC",   // subtle alt rows, hover states
    surfaceSunken:  "#EFEDE5",   // recessed areas

    // Borders — warm to match canvas
    border:         "#E5E1D8",
    borderStrong:   "#C8C3B5",
    borderFocus:    "#1B3A57",

    // Text
    ink:            "#0E1116",   // body — near-black, slightly warm
    inkMuted:       "#5D6470",   // secondary text
    inkSubtle:      "#8A8B85",   // tertiary, captions
    inkOnDark:      "#FAFAF7",   // text on navy
    inkOnAccent:    "#FFFFFF",   // text on orange

    // Dark surface (sidebar, top bar)
    dark:           "#0E1B2C",   // deeper than primary, for chrome
    darkAlt:        "#15263C",
    darkBorder:     "#1F3550",
  },

  // ─── TYPOGRAPHY ────────────────────────────────────────────────────────────
  // Display: distinctive but professional. We use Geist for the modern-tech feel.
  // Body: Inter for absolute legibility on data-heavy screens.
  // Mono: JetBrains Mono for technical identifiers (RFI codes, item nos).
  font: {
    display: `"Geist", "General Sans", "Inter Tight", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`,
    body:    `"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`,
    mono:    `"JetBrains Mono", "SF Mono", "IBM Plex Mono", Consolas, monospace`,
  },

  // Type scale — modular, restrained
  text: {
    xs:   { size: "11px", line: "16px", weight: 500, tracking: "0.02em" },
    sm:   { size: "12px", line: "18px", weight: 500, tracking: "0.005em" },
    base: { size: "13px", line: "20px", weight: 500, tracking: "0"       },
    md:   { size: "14px", line: "22px", weight: 500, tracking: "0"       },
    lg:   { size: "16px", line: "24px", weight: 600, tracking: "-0.005em"},
    xl:   { size: "20px", line: "28px", weight: 700, tracking: "-0.015em"},
    "2xl":{ size: "28px", line: "34px", weight: 700, tracking: "-0.025em"},
    "3xl":{ size: "36px", line: "42px", weight: 800, tracking: "-0.03em" },
    // Eyebrow caps — for section labels, badge text
    eyebrow: { size: "10px", line: "14px", weight: 700, tracking: "0.12em" },
  },

  // ─── SPACING ───────────────────────────────────────────────────────────────
  // 4px base. Use these named values instead of arbitrary numbers.
  space: {
    "0":  "0",
    "1":  "4px",
    "2":  "8px",
    "3":  "12px",
    "4":  "16px",
    "5":  "20px",
    "6":  "24px",
    "7":  "32px",
    "8":  "40px",
    "9":  "48px",
    "10": "64px",
  },

  // ─── RADIUS ────────────────────────────────────────────────────────────────
  radius: {
    none: "0",
    xs:   "3px",   // chips, tags
    sm:   "6px",   // inputs, buttons
    md:   "8px",   // cards, dialogs (default)
    lg:   "12px",  // big cards
    xl:   "16px",  // hero cards
    full: "999px",
  },

  // ─── SHADOWS ───────────────────────────────────────────────────────────────
  // Three tiers. Engineered, not decorative.
  shadow: {
    none: "none",
    sm:   "0 1px 2px rgba(14, 17, 22, 0.04), 0 1px 1px rgba(14, 17, 22, 0.03)",
    md:   "0 2px 4px rgba(14, 17, 22, 0.04), 0 4px 12px rgba(14, 17, 22, 0.06)",
    lg:   "0 4px 8px rgba(14, 17, 22, 0.04), 0 12px 32px rgba(14, 17, 22, 0.08)",
    // Inset for sunken inputs
    inset: "inset 0 1px 2px rgba(14, 17, 22, 0.05)",
    // Focus ring — uses accent
    focus: "0 0 0 3px rgba(255, 107, 53, 0.25)",
  },

  // ─── MOTION ────────────────────────────────────────────────────────────────
  motion: {
    fast:   "120ms cubic-bezier(0.4, 0, 0.2, 1)",
    base:   "180ms cubic-bezier(0.4, 0, 0.2, 1)",
    slow:   "280ms cubic-bezier(0.4, 0, 0.2, 1)",
    spring: "320ms cubic-bezier(0.34, 1.56, 0.64, 1)",  // gentle overshoot for cards
  },

  // ─── Z-INDEX ───────────────────────────────────────────────────────────────
  z: {
    base:      0,
    raised:    10,
    sticky:    50,
    overlay:   100,
    modal:     1000,
    tooltip:   2000,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// CSS VARIABLES — drop these on :root so non-React CSS can use them too,
// and so we can hot-swap to dark mode later by re-setting them on [data-theme].
// ─────────────────────────────────────────────────────────────────────────────
export const cssVars = {
  "--c-primary":       tokens.color.primary,
  "--c-primary-hover": tokens.color.primaryHover,
  "--c-primary-soft":  tokens.color.primarySoft,
  "--c-accent":        tokens.color.accent,
  "--c-accent-hover":  tokens.color.accentHover,
  "--c-accent-soft":   tokens.color.accentSoft,
  "--c-success":       tokens.color.success,
  "--c-warning":       tokens.color.warning,
  "--c-danger":        tokens.color.danger,
  "--c-info":          tokens.color.info,
  "--c-canvas":        tokens.color.canvas,
  "--c-surface":       tokens.color.surface,
  "--c-surface-alt":   tokens.color.surfaceAlt,
  "--c-border":        tokens.color.border,
  "--c-ink":           tokens.color.ink,
  "--c-ink-muted":     tokens.color.inkMuted,
  "--c-dark":          tokens.color.dark,
  "--c-dark-alt":      tokens.color.darkAlt,
  "--font-display":    tokens.font.display,
  "--font-body":       tokens.font.body,
  "--font-mono":       tokens.font.mono,
  "--shadow-sm":       tokens.shadow.sm,
  "--shadow-md":       tokens.shadow.md,
  "--shadow-lg":       tokens.shadow.lg,
  "--motion-base":     tokens.motion.base,
  "--motion-fast":     tokens.motion.fast,
};

// Backwards-compat shim — sections that imported the old `DS` shape
// (DS.blue, DS.muted, DS.r8...) can keep working while we migrate.
// New code should use `tokens` directly.
export const DS = {
  blue:       tokens.color.primary,
  blueHover:  tokens.color.primaryHover,
  blueSoft:   tokens.color.primarySoft,
  teal:       tokens.color.success,
  tealSoft:   tokens.color.successSoft,
  amber:      tokens.color.warning,
  amberSoft:  tokens.color.warningSoft,
  purple:     tokens.color.info,
  purpleSoft: tokens.color.infoSoft,
  red:        tokens.color.danger,
  redSoft:    tokens.color.dangerSoft,
  orange:     tokens.color.accent,
  orangeSoft: tokens.color.accentSoft,
  muted:      tokens.color.inkMuted,
  border:     tokens.color.border,
  surface:    tokens.color.surfaceAlt,
  white:      tokens.color.surface,
  text:       tokens.color.ink,
  r8:         tokens.radius.sm,
  r12:        tokens.radius.md,
  r16:        tokens.radius.lg,
  r20:        tokens.radius.xl,
  shadowSm:   tokens.shadow.sm,
};

export default tokens;
